/**
 * Adapter to convert field registry to MUI DataGrid columns
 * for the Products Row Grouping POC
 */

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import {
  FLAT_PRODUCT_FIELDS,
  GROUPABLE_FIELD_KEYS,
} from './ProductsRowGroupingDataGrid.fields';
import { FlatProductRow } from '../fixtures/flatProductData';
import { getProductDisplayName } from '../utils/productNameHelpers';
import { renderBooleanCell } from '../shared/utils/cellRenderers';
import { getGridFilterOperatorsForType } from '../utils/fieldOperators';
import DisplayNameSourceIndicator from './DisplayNameSourceIndicator';
import {
  DisplayNameSource,
  DISPLAY_NAME_SOURCE,
} from '../services/api/domains/products/types';

/**
 * Convert field registry to DataGrid column definitions
 */
export function toDataGridColumns(): GridColDef<FlatProductRow>[] {
  return FLAT_PRODUCT_FIELDS.map(field => {
    const colDef: GridColDef<FlatProductRow> = {
      field: field.key as string,
      headerName: field.label,
      width: getColumnWidth(field.key as string),
      sortable: field.isSortable ?? false,
      filterable: field.isFilterable ?? false,
      hideable: true,
    };

    // Mark groupable columns
    if (
      GROUPABLE_FIELD_KEYS.includes(
        field.key as (typeof GROUPABLE_FIELD_KEYS)[number]
      )
    ) {
      colDef.groupable = true;
    }

    // Special handling for preferred_product_name - fallback to product_cas_name
    if (field.key === 'preferred_product_name') {
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => {
        const displayName = getProductDisplayName(params.row);
        return (
          <DisplayNameSourceIndicator
            displayNameSource={
              params.row.display_name_source as DisplayNameSource | null
            }
            sourceLabel={
              params.row.display_name_source ===
              DISPLAY_NAME_SOURCE.MANUFACTURER
                ? (params.row.manufacturer_name ?? undefined)
                : undefined
            }>
            {displayName}
          </DisplayNameSourceIndicator>
        );
      };
      // groupingValueGetter controls the key used for tree construction —
      // ensures rows with null preferred_product_name group under the CAS name
      // instead of being skipped to the next grouping level.
      colDef.groupingValueGetter = (value, row) =>
        getProductDisplayName(row as FlatProductRow);
    }

    // Boolean formatting
    if (field.dataType === 'boolean') {
      colDef.type = 'boolean';
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => {
        const value = params.row[field.key as keyof FlatProductRow];
        return renderBooleanCell(value);
      };
    }

    // Explicit filter operators for all filterable non-boolean columns.
    // Overrides MUI's default operator set so only backend-supported operators
    // (including isEmpty / isNotEmpty) appear — no MUI-only operators like
    // doesNotContain, startsWith, isAnyOf which the backend cannot process.
    if (field.isFilterable && field.dataType !== 'boolean') {
      colDef.filterOperators = getGridFilterOperatorsForType(
        field.dataType as 'string' | 'number' | 'date'
      );
    }

    // NDC linked group header formatting
    // renderCell applies to data cells only; group headers use valueFormatter
    if (field.key === 'is_ndc_linked') {
      colDef.valueFormatter = (value: boolean | null | undefined) => {
        if (value === null || value === undefined) return '—';
        return value ? '✓ NDC Linked' : '✗ Not NDC Linked';
      };
    }

    // Date formatting
    if (field.dataType === 'date') {
      colDef.type = 'date';
    }

    return colDef;
  });
}

/**
 * Get default column widths based on field type
 */
function getColumnWidth(key: string): number {
  const widthMap: Record<string, number> = {
    product_cas_name: 250,
    preferred_product_name: 250,
    cas_number: 150,
    is_cas_verified: 120,
    is_fda_linked: 120,
    is_ndc_linked: 120,
    molecular_formula: 180,
    molecular_mass: 150,
    note: 300,
    uri: 200,
    inchi: 250,
    inchi_key: 200,
    smile: 250,
    canonical_smile: 250,
    manufacturer_name: 200,
    manufacturing_status: 180,
    facility_name: 200,
    country_code: 120,
    facility_status: 150,
    grade_name: 150,
    therapeutic_area_name: 200,
    created_date: 180,
    modified_date: 180,
  };

  return widthMap[key] ?? 150;
}

/**
 * Get column visibility model based on current grouping
 * Hides columns that are actively used for grouping
 */
export function getColumnVisibilityModel(
  groupingModel: string[]
): Record<string, boolean> {
  const visibilityModel: Record<string, boolean> = {};

  // Hide columns that are used for grouping
  groupingModel.forEach(fieldKey => {
    visibilityModel[fieldKey] = false;
  });

  return visibilityModel;
}

/**
 * Get all column keys
 */
export function getAllColumnKeys(): string[] {
  return FLAT_PRODUCT_FIELDS.map(field => field.key as string);
}
